import duration from "dayjs/plugin/duration";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import React, { useEffect } from "react";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Slider,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import dayjs, { type Dayjs } from "dayjs";
import type { Item, QuantityData } from "../lib/api";

dayjs.extend(duration);

const DATE_FORMAT = "MM/DD/YYYY";

interface InventoryItemProps {
  item: Item;
  setQuantity: (key: string, quantities: QuantityData[]) => void;
}

function formatQuantity(quantityLiter: number): string {
  return `${(quantityLiter * 1000).toFixed(1)}ml`;
}

export default function InventoryItem({
  item,
  setQuantity,
}: InventoryItemProps) {
  const [quantities, setQuantities] = React.useState<QuantityData[]>([]);
  const [inputDate, setInputDate] = React.useState<Dayjs | null>(null);
  const [inputSize, setInputSize] = React.useState<number | null>(
    item.sizes[0] ?? null
  );
  const [inputQuantityInt, setInputQuantityInt] = React.useState(0);
  const [inputQuantityDecimal, setInputQuantityDecimal] = React.useState(0);

  const inputQuantity = inputQuantityInt + inputQuantityDecimal;

  const addButtonOnClick = () => {
    if (!inputDate || inputQuantity <= 0) {
      window.alert("Invalid input date or quantity");
      return;
    }
    const dateString = inputDate.format(DATE_FORMAT);
    if (
      dayjs.duration(dayjs().diff(inputDate)).asDays() > item.expiration_days
    ) {
      window.alert(
        `${item.name} has expiration days of ${item.expiration_days}, but the date made is ${dateString}. Please throw it away`
      );
      return;
    }
    if (
      quantities.some((q) => q.date_made === dateString && q.size === inputSize)
    ) {
      window.alert(
        `Date ${dateString} size ${inputSize} already exists. If you wish to update the quantity, please remove it first.`
      );
      return;
    }
    setQuantities((prev) => [
      ...prev.filter((q) => q.date_made !== dateString || q.size !== inputSize),
      {
        date_made: dateString,
        count: inputQuantity,
        size: inputSize,
      },
    ]);
    setInputDate(null);
    setInputQuantityInt(0);
    setInputQuantityDecimal(0);
  };

  const removeButtonOnClick = (date: string, size: number | null) => {
    setQuantities((prev) => [
      ...prev.filter((q) => q.date_made !== date || q.size !== size),
    ]);
  };

  useEffect(() => {
    setQuantity(item.name, quantities);
  }, [setQuantity, item.name, quantities]);

  return (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          justifyContent="space-between"
          flex={1}
          sx={{ pr: 1 }}
        >
          <Typography component="span">{item.name}</Typography>
          {quantities.length > 0 && (
            <Chip size="small" label={quantities.length} color="primary" />
          )}
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ borderTop: "1px solid #d9dadb", pt: 2 }}>
        <Stack spacing={2}>
          <Stack
            spacing={2}
            sx={{
              border: "1px solid #d9dadb",
              backgroundColor: quantities.length > 0 ? "#3874CB1A" : "#F9FAFB",
              pl: 2,
              pr: 1,
              py: 1,
              borderRadius: 1,
            }}
          >
            {quantities
              .sort((q1, q2) =>
                `${q1.date_made}${q1.size}` > `${q2.date_made}${q2.size}`
                  ? 1
                  : -1
              )
              .map(({ date_made, count, size }) => (
                <Stack
                  key={`${date_made}-${size}`}
                  spacing={2}
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Stack spacing={2} direction="row">
                    <Typography>Date: {date_made}</Typography>
                    {size && <Typography>Size: {size}L</Typography>}
                    <Typography>
                      Quantity: {count} {item.unit}{" "}
                      {size && formatQuantity(count * size)}
                    </Typography>
                  </Stack>
                  <Button
                    variant="contained"
                    color="warning"
                    size="small"
                    onClick={() => removeButtonOnClick(date_made, size)}
                  >
                    Remove
                  </Button>
                </Stack>
              ))}
            {quantities.length === 0 && (
              <Typography sx={{ opacity: 0.5 }}>No entries yet</Typography>
            )}
          </Stack>
          <Typography variant="h6">Add new entry</Typography>
          <Stack direction="row" spacing={2} alignItems="center">
            <Typography component="label">Date produced</Typography>
            <DatePicker
              value={inputDate}
              onChange={(newValue) => setInputDate(newValue)}
              disableFuture={true}
            />
          </Stack>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems={{ md: "center" }}
          >
            {item.sizes.length > 0 && (
              <Stack direction="row" spacing={2} alignItems="center">
                <Typography component="label">Container size</Typography>
                <ToggleButtonGroup
                  color="primary"
                  value={inputSize}
                  exclusive
                  onChange={(_, newSize) => setInputSize(newSize)}
                  aria-label="Platform"
                >
                  {item.sizes.map((size) => (
                    <ToggleButton key={size} value={size}>
                      {size}L
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Stack>
            )}
            <Stack direction="row" alignItems="center" spacing={2}>
              <Typography>Whole containers</Typography>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="outlined"
                  onClick={() => setInputQuantityInt((prev) => prev - 1)}
                  disabled={inputQuantity < 1}
                >
                  -1
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => setInputQuantityInt((prev) => prev + 1)}
                >
                  +1
                </Button>
              </Stack>
            </Stack>
            <Stack
              direction="row"
              alignItems="center"
              spacing={2}
              sx={{ flex: 1 }}
            >
              <Typography>Partial containers</Typography>
              <Slider
                sx={{ flex: 1 }}
                value={inputQuantityDecimal}
                onChange={(_, newValue) => {
                  if (typeof newValue === "number") {
                    setInputQuantityDecimal(newValue);
                  }
                }}
                valueLabelDisplay="auto"
                shiftStep={0.1}
                step={0.1}
                marks
                min={0}
                max={0.9}
              />
            </Stack>
          </Stack>
          <Box sx={{ width: "100%", textAlign: "center" }}>
            <Typography color="primary">
              Calculated quantity: {inputQuantity} {item.unit}{" "}
              {inputSize && formatQuantity(inputQuantity * inputSize)}
            </Typography>
          </Box>
          <Button
            variant="contained"
            disabled={inputQuantity <= 0 || !inputDate}
            onClick={addButtonOnClick}
          >
            Add
          </Button>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}
